import { useState, useMemo } from 'react';
import { DEFAULT_DATA } from '../utils/constants';
import { useAuthAndSync } from './useAuthAndSync';
import { useDataActions } from './useDataActions';
import { useCalculationEngine } from './useCalculationEngine';
import { getFirestore, doc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { getAuth, signOut } from "firebase/auth"; 
import { initializeApp } from "firebase/app"; 

// ... (Firebase Config & Init Block remains unchanged) ...
const firebaseConfig = {
    apiKey: "AIzaSyDdoYtxRVi-J7eEDqrptUa9n1WFVuAuKJg",
    authDomain: "finance-os-81cd5.firebaseapp.com",
    projectId: "finance-os-81cd5",
    storageBucket: "finance-os-81cd5.firebasestorage.app",
    messagingSenderId: "654314106760",
    appId: "1:654314106760:web:46ffc410b5aa966816543d"
};

let app, auth, db;
try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
} catch (e) {
    console.log("Firebase init error (ignore if offline):", e);
}

// Define Global State structure
const getInitialState = (defaultData) => {
    const EXTENDED_DEFAULT = {
        ...defaultData,
        trackerHistory: [],
        pendingBudgetAdjustment: 0,
        sinkingFunds: [], 
        additionalIncome: [],
        withdrawalRate: 4.0, 
        badges: [], 
        flags: { hasSimulatedDebt: false },
        scenario: { active: false, goals: [] },
        username: null,       
        elementalAvatar: 'air', 
        friends: [],
        readArticles: [],
        trackerOffset: 0, // Ensure this exists
        lastModified: 0
    };
    const local = window.localStorage.getItem('sf_data_bundle'); 
    return local ? { ...EXTENDED_DEFAULT, ...JSON.parse(local) } : EXTENDED_DEFAULT; 
};

export function useFinancialData() {
    // 1. Core Application State
    const [data, setData] = useState(() => getInitialState(DEFAULT_DATA));
    // Removed independent [trackerOffset, setTrackerOffset] state
    
    const [simulateVolatility, setSimulateVolatility] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [newBadge, setNewBadge] = useState(null); 

    // 3. Initialize Modular Hooks
    const { auth: hookAuth, db: hookDb } = useAuthAndSync(data, setData, setUser, setLoading, setIsSaving, DEFAULT_DATA);
    const coreActions = useDataActions(data, setData);
    
    // Pass data.trackerOffset (from Global State) instead of local state
    const calculations = useCalculationEngine(data, data.trackerOffset || 0, simulateVolatility);
    
    // Helper to get auth/db safely
    const getFirebaseRefs = () => ({ auth: hookAuth || auth, db: hookDb || db });

    // --- 4. WRAPPED ACTIONS ---
    const updateProfile = async (updates) => {
        const { db } = getFirebaseRefs();
        coreActions.updateProfile(updates);
        if (user && db) {
            await setDoc(doc(db, "users", user.uid), { ...data, ...updates }, { merge: true })
                .catch(e => console.error("Profile Update Error", e));
        }
    };
    
    const addFriend = async (targetUsername) => {
        const { db } = getFirebaseRefs();
        if (!user || !db) return { success: false, message: "You must be logged in/online." };
        if (!targetUsername) return { success: false, message: "Enter a username." };
        if (targetUsername === data.username) return { success: false, message: "You cannot add yourself." };

        const alreadyFriend = (data.friends || []).some(f => f.username.toLowerCase() === targetUsername.toLowerCase());
        if (alreadyFriend) return { success: false, message: "Already in your circle." };

        try {
            const q = query(collection(db, "users"), where("username", "==", targetUsername));
            const querySnapshot = await getDocs(q);
            if (querySnapshot.empty) return { success: false, message: "User not found." };
            
            const friendDoc = querySnapshot.docs[0].data();
            const friendData = {
                uid: querySnapshot.docs[0].id,
                username: friendDoc.username,
                elementalAvatar: friendDoc.elementalAvatar || 'air',
                addedAt: new Date().toISOString()
            };
            const newFriends = [...(data.friends || []), friendData];
            coreActions.addFriend({ username: friendData.username, elementalAvatar: friendData.elementalAvatar, addedAt: friendData.addedAt });
            await setDoc(doc(db, "users", user.uid), { friends: newFriends }, { merge: true });
            return { success: true, message: `Connected with ${targetUsername}!` };

        } catch (error) {
            console.error("Add Friend Error:", error);
            return { success: false, message: "Connection failed. Try again." };
        }
    };

    const removeFriend = async (friendUsername) => {
        const { db } = getFirebaseRefs();
        coreActions.removeFriend(friendUsername);
        const newFriends = (data.friends || []).filter(f => f.username !== friendUsername);
        if (user && db) {
            await setDoc(doc(db, "users", user.uid), { friends: newFriends }, { merge: true });
        }
    };

    const markArticleRead = (articleId) => {
        const { db } = getFirebaseRefs();
        coreActions.markArticleRead(articleId);
        if (user && db) {
             const newRead = [...(data.readArticles || []), articleId];
             setDoc(doc(db, "users", user.uid), { readArticles: newRead }, { merge: true }).catch(console.error);
        }
    };

    const resetData = async () => {
        if (!window.confirm("Are you sure? This will WIPE ALL DATA and restart your journey. This cannot be undone.")) return;
        coreActions.resetData(); 
        if (user) {
            const cleanState = getInitialState(DEFAULT_DATA);
            const { db } = getFirebaseRefs();
            if(db) await setDoc(doc(db, "users", user.uid), cleanState); 
        }
        window.location.reload(); 
    };

    const handleLogout = async () => { 
        const { auth } = getFirebaseRefs();
        if (auth) {
            await signOut(auth); 
        }
    };

    // --- 5. Return Final Merged Object ---
    const actions = useMemo(() => ({
        ...coreActions, 
        updateProfile,
        addFriend,
        removeFriend,
        markArticleRead,
        resetData,
        handleLogout, 
        setSimulateVolatility,
        clearNewBadge: () => setNewBadge(null),
    }), [data, simulateVolatility, user, hookAuth, hookDb, coreActions]); 

    return { 
        data, loading, user, auth: hookAuth, 
        trackerOffset: data.trackerOffset || 0, // Pass data-derived offset
        simulateVolatility, newBadge, isSaving,
        actions: actions,
        calculations: { 
            ...calculations, 
            currentNetWorth: calculations.currentNetWorth, 
        } 
    };
}